<script lang="ts" generics="TValues, TValue, TItem">
    import type { Field, Form } from '@jmvdevelop/svelte-form';
    import Input from '$components/Form/Input.svelte';
    import { cx } from '$lib/utils/cx';
    import StackZIndex from '$components/ZIndex/StackZIndex.svelte';
    import { fly } from 'svelte/transition';
    import {
        createCombobox,
        melt,
        createSync,
        type ComboboxOptionProps,
    } from '@melt-ui/svelte';
    import { createResizeObserver } from '$lib/utils/resizeObserver';

    export let form: Form<TValues, string>;
    export let queryField: Field<TValues, string | null>;
    export let itemField: Field<TValues, TValue | null> | null = null;

    export let label: string = '';
    export let placeholder: string = '';
    export let items: TItem[];
    export let toOption: (item: TItem) => ComboboxOptionProps<TValue>;
    export let thinLeftPad: boolean = false;
    export let zIndex: number | 'current' | 'inc' = 'inc';

    // This function is called when items can be loaded. When the component is closed, there is no point in preloading the items.
    // It can be called several times
    export let onItemCanBeLoaded: () => void;

    const { useResizeObserver, entries } = createResizeObserver();
    $: referenceWidth = $entries[0]?.contentRect?.width ?? 0;

    const q = form.stores().fieldValue(queryField);

    const {
        elements: { menu, input, option, label: elementLabel },
        states: { inputValue, open },
    } = createCombobox<TValue>({
        preventScroll: false,
        ids: { trigger: form.getFieldId(queryField) },
        forceVisible: true,
        loop: true,
        positioning: {
            placement: 'bottom',
            flip: false,
            offset: {
                crossAxis: thinLeftPad ? 17 : 11,
                mainAxis: 0,
            },
            gutter: 0,
            sameWidth: true,
        },
        onSelectedChange: ({ next }) => {
            if (next != null) {
                form.dispatch(form.actions().changeValue(next.label ?? '', queryField));
                if (itemField != null) {
                    form.dispatch(form.actions().changeValue(next.value, itemField));
                }
            } else {
                if (itemField != null) {
                    form.dispatch(form.actions().changeValue(null, itemField));
                }
            }

            return next;
        },
    });

    const sync = createSync({ inputValue });
    $: sync.inputValue($q ?? '', (v) => {
        form.dispatch(form.actions().changeValue(v, queryField));
    });

    $: $open && ($q ?? '').trim() !== '' ? onItemCanBeLoaded() : undefined;

    interface $$Slots {
        item: {
            item: TItem;
        };
    }
</script>

<StackZIndex {zIndex} let:zIndex>
    <div class="relative h-full flex-auto" use:useResizeObserver>
        <div
            class={cx(
                'h-full rounded-[50px] flex items-center',
                thinLeftPad ? 'pl-[16px] pr-[32px]' : 'px-[32px]',
                'py-[13px] focus-within:shadow-search-header-input',
                'transition-shadow z-[--z-index] bg-white relative',
            )}
            style={`--z-index: ${zIndex + 1}`}
        >
            <div class="flex flex-col grow">
                <!-- $label contains the 'for' attribute -->
                <!-- svelte-ignore a11y-label-has-associated-control -->
                <label
                    use:melt={$elementLabel}
                    class="text-13 font-bold text-gray-800 m-0"
                >
                    {label}
                </label>

                <Input
                    defaultClass="border-0 outline-none text-gray-800 placeholder:text-gray-800 placeholder:opacity-50 h-[20px]"
                    {placeholder}
                    {form}
                    field={queryField}
                    toText={(t) => t}
                    fromText={(t) => t}
                    meltAction={input}
                    {...$input}
                />
            </div>

            <button
                type="button"
                on:click={() => {
                    form.dispatch(form.actions().changeValue('', queryField));
                }}
                class={cx(
                    ($q ?? '') !== '' ? 'visible' : 'invisible',
                    'btn-reset',
                    'rounded-full',
                    'size-[20px]',
                    'bg-gray-500 text-white',
                    'hover:opacity-80',
                    'flex items-center justify-center',
                )}
                aria-label="clear"
            >
                <i class="tnp tnp-cross-small" aria-hidden={true} />
            </button>
        </div>
        {#if $open && ($q ?? '').trim() !== ''}
            <div
                use:melt={$menu}
                class="z-[--z-index] flex flex-col max-h-[364px] overflow-hidden bg-gray-100 w-[--var-width] m-0 mt-[-24px] pt-[32px] p-0 shadow-search-header-dropdown rounded-b-[32px]"
                style={`--z-index: ${zIndex}; --var-width: ${referenceWidth}px;`}
                transition:fly={{ duration: 150, y: -5 }}
            >
                <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
                <ul
                    class="flex max-h-full flex-col overflow-y-auto py-[16px] list-unstyled"
                    tabindex="0"
                >
                    {#each items as item}
                        <li
                            use:melt={$option(toOption(item))}
                            class={cx(
                                'relative cursor-pointer scroll-my-2',
                                'py-[12px] px-[20px]',
                                'hover:bg-gray-200 data-[highlighted]:bg-gray-300',
                            )}
                        >
                            <slot name="item" {item} />
                        </li>
                    {/each}
                </ul>
            </div>
        {/if}
    </div>
</StackZIndex>
